import * as vp from 'vopay';
import { useEffect, useRef, useState } from 'react';

import useFetchPartnerAccountSummaryDetails from '@/hooks/useApi/useFetchPartnerAccountSummaryDetails';
import useFileDownload from '@/hooks/useFiledDownload';
import { usePermissions } from '@/providers/Permissions.provider';
import { usePartnerData } from '@/providers/PartnerData.provider';
import { useTranslation } from 'react-i18next';

const { Box, Button, SubTitle, Table, Title } = vp;

type ParamsType = {
  Year: string;
  Month: string;
  VopayAccountID: string;
};

const ActivitySummaryDetails = (): React.JSX.Element => {
  const {
    response: { isRoleViewOnly },
  } = usePartnerData();
  const [partnerAccountSummaryDetails, getPartnerAccountSummaryDetails] = useFetchPartnerAccountSummaryDetails({});
  const [accountSummaryDetails, setAccountSummaryDetails] = useState<vp.PartnerAccountSummaryDetailsResponse>();
  const [dataFetched, setDataFetched] = useState<boolean>(false);
  const urlParams = new URL(window.location.toString()).searchParams;
  const { downloadJson } = useFileDownload();
  const { permissions } = usePermissions();
  const firstUpdate = useRef(true);
  const { t } = useTranslation('translations', { keyPrefix: 'Reports.ActivitySummaryDetails' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const params: ParamsType = {
    Year: urlParams.get('Year') || '',
    Month: urlParams.get('Month') || '',
    VopayAccountID: urlParams.get('VopayAccountID') || '',
  };

  useEffect(() => {
    getPartnerAccountSummaryDetails(params);
  }, []);

  useEffect(() => {
    setAccountSummaryDetails(partnerAccountSummaryDetails);
    if (firstUpdate.current) {
      firstUpdate.current = false;
    } else {
      setDataFetched(true);
    }
  }, [partnerAccountSummaryDetails]);

  return (
    <Box flx="col">
      <Title title={t('pageTitle')} />
      <Box pt="sm" pb="sm" jc="fe">
        <Button
          id="invoicing-details-account-json-download"
          size="sm"
          label={t('downloadJSONButton')}
          icon="download"
          iconLocation="right"
          iconSize="md"
          isDisabled={isRoleViewOnly}
          onClick={() => downloadJson(accountSummaryDetails, getDownloadFileName())}
        />
      </Box>
      {dataFetched && accountSummaryDetails && Object.keys(accountSummaryDetails.AccountSummary).length === 0 && (
        <SubTitle subTitle={t('noAvailableData')} />
      )}
      {buildDetailTables()}
    </Box>
  );

  function buildDetailTables() {
    if (accountSummaryDetails) {
      return Object.entries(accountSummaryDetails.AccountSummary).map(([currency, usernames]) =>
        Object.values(usernames).map((services) => {
          return Object.entries(services).map(([service, values]) => {
            const tableProps: vp.TableProps = {
              name: `${service}-${currency}-${service}`,
              variant: 'pair',
              columns: {
                key: { header: tGlobal('Tables.key') },
                value: { header: tGlobal('Tables.value') },
              },
              rows: [
                {
                  key: t('Table.totalRevenueGenerated', { defaultValue: 'Revenue Generated' }),
                  value: `$${Math.round(Number(values.TotalRevenueGenerated) * 100) / 100}`,
                },
                {
                  key: t('Table.partnerIncome', { defaultValue: 'Partner Income' }),
                  value: permissions.ShowPartnerIncome
                    ? `$${Math.round(Number(values.PartnerIncome) * 100) / 100}`
                    : '--',
                },
              ],
            };

            return (
              <Box key={`${service}-${currency}`} mb="lg" flx="col">
                <Box pt="lg" flx="col">
                  <strong>{`${service} | ${currency}`}</strong>
                  <Table {...tableProps} />
                </Box>
              </Box>
            );
          });
        }),
      );
    } else {
      return <></>;
    }
  }

  function getDownloadFileName() {
    return `activity-summary-details-${params.Month}-${params.Year}`;
  }
};

export default ActivitySummaryDetails;
